import React from 'react'
import {
  faArrowTurnDownLeft,
  faCircleXmark,
  faTag,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { AvailableMobileLoyaltyRewardResponse } from '@/lib/apis/types'
import { faCheck, faStar } from '@fortawesome/pro-solid-svg-icons'
import { RATINGS } from '@/lib/social-proof'

const BENEFITS = [
  'Fill your script yourself or have it delivered to your door',
  'Unlimited check-ins with your doctor for dose adjustments',
  'No price changes on the script-only plan',
]

function formatCents(cents: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(cents / 100)
}

function compactCents(cents: number) {
  return formatCents(cents).replace(/\.00$/, '')
}

export function EdPricingDetails({
  baseAmountCents,
  rewardResponse,
}: {
  baseAmountCents: number
  rewardResponse?: AvailableMobileLoyaltyRewardResponse
}) {
  const quote = rewardResponse?.estimatedQuote
  const appliedQuote = quote && quote.rewardAmountCents > 0 ? quote : null
  // The backend quote is authoritative when a reward applies; the CMS price can
  // lag behind Stripe for a few minutes after a price change.
  const consultationCents = appliedQuote?.originalAmountCents ?? baseAmountCents
  const dueTodayCents = appliedQuote?.payableAmountCents ?? consultationCents
  const reward = rewardResponse?.reward
  const offerValue =
    reward?.discountType === 'PERCENTAGE_BPS'
      ? `${reward.discountValue / 100}%`
      : appliedQuote
        ? compactCents(appliedQuote.rewardAmountCents)
        : null

  return (
    <section
      className="mx-auto mb-16 flex w-full max-w-3xl flex-col gap-5 font-sans text-[#0B0B0F] sm:gap-6"
      data-testid="ed-pricing"
      aria-labelledby="ed-pricing-title"
    >
      <h1
        id="ed-pricing-title"
        className="text-xl font-medium leading-7 tracking-[-0.025em] text-gray-900 sm:text-2xl sm:leading-8"
      >
        Your ED treatment plan
      </h1>

      <div className="-mx-6 overflow-hidden border-y border-gray-200 sm:mx-0 sm:rounded-xl sm:border">
        <div className="flex flex-col px-6 pt-5 sm:pt-6">
          <div className="flex flex-col gap-1 pb-4 sm:pb-[18px]">
            <p className="text-xs font-bold uppercase leading-4 tracking-wide">
              ED treatment plan
            </p>
            <h2 className="text-lg font-semibold leading-6">
              Initial assessment + first 2 months of treatment
            </h2>
          </div>

          {appliedQuote && (
            <div
              className="flex items-center justify-between gap-4 border-t border-gray-200 py-3.5"
              data-testid="ed-offer-applied"
            >
              <div className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faTag} className="size-4 shrink-0" />
                <div className="space-y-0.5">
                  <p className="text-base font-medium leading-6 text-gray-900">
                    {reward?.title ?? 'Your offer'}
                  </p>
                  <p className="text-sm leading-5 text-gray-600">
                    {offerValue} off your initial assessment
                  </p>
                </div>
              </div>
              <span className="shrink-0 text-base font-semibold leading-6">
                −{compactCents(appliedQuote.rewardAmountCents)}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 border-t border-gray-200 pb-5 pt-4 sm:pb-[22px] sm:pt-[18px]">
            <div className="min-w-0 flex-1 space-y-0.5">
              <h2 className="text-lg font-semibold leading-6">Due today</h2>
              <p className="text-sm leading-5 text-gray-600">
                Refunded if you’re not eligible.
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2.5">
              {appliedQuote && (
                <span
                  className="text-base font-medium leading-6 text-gray-400 line-through"
                  data-testid="ed-original-price"
                >
                  {compactCents(consultationCents)}
                </span>
              )}
              <span className="rounded-md bg-[#F2FF5E] px-2 py-0.5 sm:px-2.5">
                <span
                  className="font-display text-[32px] font-extrabold leading-9 tracking-[-0.03em] sm:text-[36px] sm:leading-10"
                  data-testid="ed-due-today"
                >
                  {compactCents(dueTodayCents)}
                </span>
              </span>
            </div>
          </div>
          <div className="h-px w-full shrink-0 bg-gray-200" />
        </div>

        <div className="flex flex-col gap-5 px-6 pb-6 pt-5">
          <ul className="flex flex-col gap-3 text-[15px] leading-[22px] text-gray-900">
            {BENEFITS.map((benefit) => (
              <li
                key={benefit}
                className="flex items-start gap-3 sm:items-center"
              >
                <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F6]">
                  <FontAwesomeIcon icon={faCheck} className="size-3" />
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 text-sm leading-5 text-gray-900 sm:px-1">
        <p className="flex items-start gap-2.5">
          <FontAwesomeIcon
            icon={faArrowTurnDownLeft}
            className="mt-0.5 size-4 shrink-0 text-gray-500"
          />
          <span>
            Not eligible for the plan? We refund your{' '}
            {compactCents(dueTodayCents)}.
          </span>
        </p>
        <p className="flex items-center gap-2.5">
          <FontAwesomeIcon
            icon={faCircleXmark}
            className="size-4 shrink-0 text-gray-500"
          />
          <span>Cancel your plan anytime.</span>
        </p>
      </div>

      <div className="-mx-6 flex flex-col gap-3 border-t border-gray-200 px-6 pt-5 sm:mx-0 sm:flex-row sm:gap-0 sm:divide-x sm:divide-gray-200 sm:px-0">
        <div className="flex items-center gap-2.5 sm:flex-1 sm:flex-col sm:items-start sm:gap-1 sm:pl-1 sm:pr-5">
          <span className="flex items-center gap-2.5 font-display text-lg font-bold leading-6 tracking-tight sm:gap-1.5 sm:text-xl sm:leading-[26px]">
            {RATINGS.stars}
            <FontAwesomeIcon
              icon={faStar}
              className="size-3.5 shrink-0 sm:size-4"
            />
          </span>
          <span className="text-[13px] leading-[18px] text-gray-500">
            Average patient rating
          </span>
        </div>
        <div className="flex items-center gap-2.5 sm:flex-1 sm:flex-col sm:items-start sm:gap-1 sm:px-5">
          <span className="font-display text-lg font-bold leading-6 tracking-tight sm:text-xl sm:leading-[26px]">
            {RATINGS.patients.toLocaleString('en-AU')}+
          </span>
          <span className="text-[13px] leading-[18px] text-gray-500">
            Patients across Australia
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 sm:flex-1 sm:flex-col sm:items-start sm:gap-1 sm:pl-5 sm:pr-1">
          <span className="whitespace-nowrap font-display text-lg font-bold leading-6 tracking-tight sm:text-xl sm:leading-[26px]">
            AHPRA-registered
          </span>
          <span className="text-[13px] leading-[18px] text-gray-500">
            Australian doctors
          </span>
        </div>
      </div>
    </section>
  )
}
